'use client';
import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/store/auth.store';

const PhoneCanvas = ({ phone }: { phone: string }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const font = '14px -apple-system, BlinkMacSystemFont, Roboto, sans-serif';
    ctx.font = font;
    const width = ctx.measureText(phone).width + 8;
    canvas.width = Math.ceil(width);
    canvas.height = 20;
    ctx.font = font;
    ctx.fillStyle = '#6b7280';
    ctx.fillText(phone, 4, 15);
  }, [phone]);
  return <canvas ref={canvasRef} aria-label={phone} style={{ display: 'inline-block', verticalAlign: 'middle' }} />;
};

const ContactsBlock = ({ specialistId, hasContacts }: {
  specialistId: string;
  hasContacts: boolean;
}) => {
  const { isAuthenticated } = useAuthStore();
  const [revealed, setRevealed] = useState<{ workPhone?: string | null; telegramUsername?: string | null; maxUsername?: string | null; website?: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsCaptcha, setNeedsCaptcha] = useState(false);
  const captchaContainerRef = React.useRef<HTMLDivElement>(null);
  const captchaWidgetId = React.useRef<number | null>(null);
  const revealInProgress = React.useRef(false);

  const doReveal = async (captchaToken?: string) => {
    if (revealInProgress.current) return;
    revealInProgress.current = true;
    setLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (captchaToken) headers['x-captcha-token'] = captchaToken;
      const res = await api.post(`/specialists/${specialistId}/reveal-contacts`, {}, { headers });
      setRevealed(res.data.data);
      setNeedsCaptcha(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'RATE_LIMIT') {
        setNeedsCaptcha(true);
      } else if (code === 'DAILY_LIMIT') {
        notify.error('Лимит исчерпан. Попробуйте завтра.');
      } else if (code === 'CAPTCHA_INVALID') {
        notify.error('Капча не пройдена. Попробуйте ещё раз.');
        setNeedsCaptcha(true);
      } else {
        notify.error('Не удалось загрузить контакты');
      }
    } finally {
      setLoading(false);
      revealInProgress.current = false;
    }
  };

  useEffect(() => {
    if (!needsCaptcha) return;
    const siteKey = process.env.NEXT_PUBLIC_SMARTCAPTCHA_SITE_KEY;
    if (!siteKey) return;
    const loadCaptcha = () => {
      if ((window as any).smartCaptcha && captchaContainerRef.current) {
        captchaWidgetId.current = (window as any).smartCaptcha.render(captchaContainerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => { doReveal(token); },
        });
      }
    };
    if ((window as any).smartCaptcha) {
      setTimeout(loadCaptcha, 0);
    } else {
      const s = document.createElement('script');
      s.id = 'yandex-smartcaptcha';
      s.src = 'https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=smartCaptchaOnload';
      s.async = true;
      document.head.appendChild(s);
      (window as any).smartCaptchaOnload = loadCaptcha;
    }
    return () => {
      if (captchaWidgetId.current !== null && (window as any).smartCaptcha) {
        (window as any).smartCaptcha.destroy(captchaWidgetId.current);
        captchaWidgetId.current = null;
      }
    };
  }, [needsCaptcha]);

  if (!hasContacts) return null;

  if (!isAuthenticated()) {
    return (
      <a href="/login" className="flex items-center gap-1 text-sm text-brand border border-brand/30 rounded-lg px-3 py-1 hover:bg-brand/5 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        Войдите чтобы увидеть контакты
      </a>
    );
  }

  if (needsCaptcha) {
    return (
      <>
        <button disabled className="flex items-center gap-1 text-sm text-brand border border-brand/30 rounded-lg px-3 py-1 opacity-50">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          Показать контакты 🔓
        </button>
        <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'var(--bg-surface, #fff)',borderRadius:'16px',padding:'28px 24px',maxWidth:'500px',width:'95%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)',maxHeight:'90vh',overflowY:'auto'}}>
            <h3 style={{margin:'0 0 8px',fontSize:'16px',fontWeight:600,color:'var(--text-primary)'}}>Подтвердите, что вы не робот</h3>
            <p style={{margin:'0 0 16px',fontSize:'13px',color:'var(--text-secondary)'}}>Слишком много запросов. Пройдите проверку чтобы продолжить.</p>
            <div ref={captchaContainerRef} />
          </div>
        </div>
      </>
    );
  }

  if (!revealed) {
    return (
      <button
        onClick={() => doReveal()}
        disabled={loading}
        className="flex items-center gap-1 text-sm text-brand border border-brand/30 rounded-lg px-3 py-1 hover:bg-brand/5 transition-colors disabled:opacity-50"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        {loading ? 'Загрузка...' : 'Показать контакты 🔓'}
      </button>
    );
  }

  const { workPhone, telegramUsername, maxUsername, website } = revealed;
  return (
    <div className="flex flex-wrap items-center gap-3">
      {workPhone && (
        <button onClick={() => window.location.href = `tel:${workPhone}`} title={workPhone} className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-0 p-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28"><circle cx="12" cy="12" r="12" fill="#307479"/><path fill="white" d="M16.5 14.4l-1.9-.2c-.4 0-.8.1-1.1.4l-1.4 1.4a10.8 10.8 0 0 1-4.7-4.7l1.4-1.4c.3-.3.4-.7.4-1.1L9 6.9c-.1-.6-.6-1-1.2-1H6.5C5.8 5.9 5.2 6.5 5.3 7.2c.6 5.2 4.7 9.3 9.9 9.9.7.1 1.3-.5 1.3-1.2v-1.3c0-.6-.4-1.1-1-1.2z"/></svg>
        </button>
      )}
      {website && (
        <button onClick={() => window.open(website!.startsWith('http') ? website! : `https://${website}`, '_blank')} title={website} className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-0 p-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28"><circle cx="12" cy="12" r="12" fill="#307479"/><g stroke="white" strokeWidth="1.5" fill="none"><circle cx="12" cy="12" r="6"/><line x1="6" y1="12" x2="18" y2="12"/><path d="M12 6a9 9 0 0 1 2.5 6A9 9 0 0 1 12 18 9 9 0 0 1 9.5 12 9 9 0 0 1 12 6z"/></g></svg>
        </button>
      )}
      {telegramUsername && (
        <button onClick={() => window.open(`https://t.me/${telegramUsername!.replace('@', '')}`, '_blank')} title="Telegram" className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-0 p-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28"><circle cx="12" cy="12" r="12" fill="#2AABEE"/><path fill="white" d="M5.3 11.9 17 7.3c.6-.2 1.1.1.9.9l-2 9.3c-.1.6-.5.8-1 .5l-2.8-2.1-1.3 1.3c-.2.2-.3.3-.6.3l.2-2.9 5.2-4.7c.2-.2 0-.3-.4-.1L7.9 13.4 5.1 12.6c-.6-.2-.6-.6.2-.7z"/></svg>
        </button>
      )}
      {maxUsername && (
        <button onClick={() => window.open(maxUsername!.startsWith('http') ? maxUsername! : `https://max.ru/u/${maxUsername}`, '_blank')} title="MAX" className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-0 p-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 42" width="28" height="28" fill="none"><path fill="#307479" fillRule="evenodd" d="M21.47 41.88c-4.11 0-6.02-.6-9.34-3-2.1 2.7-8.75 4.81-9.04 1.2 0-2.71-.6-5-1.28-7.5C1 29.5.08 26.07.08 21.1.08 9.23 9.82.3 21.36.3c11.55 0 20.6 9.37 20.6 20.91a20.6 20.6 0 0 1-20.49 20.67m.17-31.32c-5.62-.29-10 3.6-10.97 9.7-.8 5.05.62 11.2 1.83 11.52.58.14 2.04-1.04 2.95-1.95a10.4 10.4 0 0 0 5.08 1.81 10.7 10.7 0 0 0 11.19-9.97 10.7 10.7 0 0 0-10.08-11.1Z" clipRule="evenodd"/></svg>
        </button>
      )}
    </div>
  );
};

export default ContactsBlock;
